import { Component } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import InputAdornment from "@mui/material/InputAdornment";
import Pagination from "@mui/material/Pagination";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";

import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";

import { request } from "app/network";
import { useMaterialUIController } from "context";
import AddPointLimitPolicyDialog from "./components/AddPointLimitPolicyDialog";

interface PointLimitPolicyListState {
    policies: any[];
    loading: boolean;
    searchTerm: string;
    pageSize: number;
    pageNumber: number;
    totalCount: number;
    addDialogOpen: boolean;
    editPolicy: any | null;
}

const PAGE_SIZE_STORAGE_KEY = "mediinstep_point_limit_policy_page_size";

class PointLimitPolicyListScreenClass extends Component<any, PointLimitPolicyListState> {
    constructor(props: any) {
        super(props);
        const savedPageSize = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        const initialPageSize = savedPageSize ? parseInt(savedPageSize, 10) : 20;

        this.state = {
            policies: [],
            loading: false,
            searchTerm: "",
            pageSize: initialPageSize,
            pageNumber: 1,
            totalCount: 0,
            addDialogOpen: false,
            editPolicy: null,
        };
    }

    componentDidMount() {
        this.loadPolicies();
    }

    loadPolicies = async () => {
        try {
            this.setState({ loading: true });

            const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";
            const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);

            const param = {
                PageNumber: this.state.pageNumber,
                PageSize: this.state.pageSize,
                SearchKeyword: this.state.searchTerm || undefined,
                TenantSeqId: savedTenantId ? parseInt(savedTenantId, 10) : undefined,
            };

            const res = await request("PointLimitPolicy.pagedList", { paramz: param });

            if (res && res.data) {
                const pagedList = res.data;
                const policies = (pagedList.items || []).map((policy: any) => ({
                    seqId: policy.seqId,
                    policyName: policy.policyName || "-",
                    limitCode: policy.limitCode || "-",
                    limitCodeName: policy.limitCodeName || policy.limitCode || "-",
                    limitValue: policy.limitValue || 0,
                    description: policy.description || "",
                    isActive: policy.isActive,
                    isDefault: policy.isDefault,
                    startDate: policy.startDate ? new Date(policy.startDate).toLocaleDateString() : "-",
                    endDate: policy.endDate ? new Date(policy.endDate).toLocaleDateString() : "-",
                    tenantName: policy.tenantName || "전체",
                }));

                this.setState({
                    policies,
                    totalCount: pagedList.totalCount || 0,
                    loading: false,
                });
            } else {
                this.setState({ loading: false });
            }
        } catch (error: any) {
            console.error("포인트 한도 정책 목록 조회 실패:", error);
            this.setState({ loading: false });
        }
    };

    handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const searchTerm = event.target.value;
        this.setState({ searchTerm, pageNumber: 1 }, () => {
            this.loadPolicies();
        });
    };

    handlePageChange = (pageIndex: number) => {
        this.setState({ pageNumber: pageIndex + 1 }, () => {
            this.loadPolicies();
        });
    };

    handlePageSizeChange = (pageSize: number) => {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize.toString());
        this.setState({ pageSize, pageNumber: 1 }, () => {
            this.loadPolicies();
        });
    };

    handleAddOpen = () => {
        this.setState({ addDialogOpen: true, editPolicy: null });
    };

    handleEditOpen = (policy: any) => {
        this.setState({ addDialogOpen: true, editPolicy: policy });
    };

    handleDialogClose = () => {
        this.setState({ addDialogOpen: false, editPolicy: null });
    };

    handleSuccess = () => {
        this.setState({ pageNumber: 1, searchTerm: "" }, () => {
            this.loadPolicies();
        });
    };

    handleDelete = async (seqId: number) => {
        if (!window.confirm("정말 삭제하시겠습니까?")) return;

        try {
            await request("PointLimitPolicy.delete", { pathParams: { id: seqId.toString() } });
            this.loadPolicies();
        } catch (error: any) {
            console.error("정책 삭제 실패:", error);
            alert("삭제에 실패했습니다.");
        }
    };

    render() {
        const { policies, loading, searchTerm, pageSize, pageNumber, totalCount } = this.state;

        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3}>
                    {/* 검색 영역 */}
                    <Card>
                        <MDBox p={3}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <MDBox display="flex" alignItems="center">
                                    <Icon fontSize="medium">policy</Icon>
                                    <MDTypography ml={1} variant="h5" fontWeight="medium">
                                        포인트 한도정책 관리 ( POINT LIMIT POLICY )
                                    </MDTypography>
                                </MDBox>
                                <MDButton
                                    variant="gradient"
                                    color="info"
                                    onClick={this.handleAddOpen}
                                >
                                    <Icon fontSize="small" sx={{ mr: 0.5 }}>
                                        add
                                    </Icon>
                                    정책 추가
                                </MDButton>
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <MDBox display="flex" alignItems="center">
                                    <Autocomplete
                                        disableClearable
                                        value={this.state.pageSize.toString()}
                                        options={["5", "10", "15", "20", "25", "30", "40", "50"]}
                                        renderInput={(params) => (
                                            <MDInput {...params} />
                                        )}
                                        size="small"
                                        sx={{ width: "5rem" }}
                                        onChange={(_, newValue) => {
                                            if (newValue) {
                                                this.handlePageSizeChange(Number(newValue));
                                            }
                                        }}
                                    />
                                    <MDTypography variant="caption" color="secondary">
                                        &nbsp;&nbsp;entries per page
                                    </MDTypography>
                                </MDBox>

                                <MDBox width="12rem" ml="auto">
                                    <MDInput
                                        placeholder="Search..."
                                        value={searchTerm}
                                        onChange={this.handleSearch}
                                        size="small"
                                        fullWidth
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Icon fontSize="small" sx={{ color: '#9e9e9e' }}>
                                                        search
                                                    </Icon>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </MDBox>
                            </MDBox>
                        </MDBox>
                    </Card>

                    {/* 리스트 영역 */}
                    <MDBox mt={3}>
                        {loading ? (
                            <MDBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="400px"
                            >
                                <CircularProgress size={40} sx={{ color: "#49a3f1" }} />
                            </MDBox>
                        ) : policies.length === 0 ? (
                            <MDBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="400px"
                            >
                                <MDTypography variant="h6" color="text">
                                    {searchTerm ? "검색 결과가 없습니다." : "등록된 정책이 없습니다."}
                                </MDTypography>
                            </MDBox>
                        ) : (
                            <>
                                <Grid container spacing={3}>
                                    {policies.map((policy) => {
                                        const gridProps = this.props.miniSidenav
                                            ? { xs: 12, sm: 6, md: 4, lg: 4, xl: 2.4 }
                                            : { xs: 12, sm: 6, md: 4, lg: 4, xl: 4 };

                                        const sxProps = this.props.miniSidenav
                                            ? {
                                                '@media (min-width: 1920px)': {
                                                    flexBasis: '16.666667%',
                                                    maxWidth: '16.666667%'
                                                }
                                            }
                                            : {
                                                '@media (min-width: 1920px)': {
                                                    flexBasis: '20%',
                                                    maxWidth: '20%'
                                                }
                                            };

                                        return (
                                            <Grid item {...gridProps} sx={sxProps} key={policy.seqId}>
                                                <Card
                                                    sx={{
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                                        '&:hover': {
                                                            transform: 'translateY(-4px)',
                                                            boxShadow: 6,
                                                        },
                                                    }}
                                                >
                                                    <MDBox p={2.5} display="flex" flexDirection="column" height="100%">
                                                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                                            <MDTypography variant="h6" fontWeight="medium">
                                                                {policy.policyName}
                                                            </MDTypography>
                                                            <MDBox display="flex" gap={0.5}>
                                                                {policy.isDefault && (
                                                                    <Chip
                                                                        label="기본"
                                                                        color="primary"
                                                                        size="small"
                                                                        sx={{
                                                                            fontWeight: "medium",
                                                                            backgroundColor: "#2196F3",
                                                                            color: "#ffffff",
                                                                            "& .MuiChip-label": {
                                                                                color: "#ffffff",
                                                                            },
                                                                        }}
                                                                    />
                                                                )}
                                                                <Chip
                                                                    label={policy.isActive ? "활성" : "비활성"}
                                                                    color={policy.isActive ? "success" : "default"}
                                                                    size="small"
                                                                    sx={{
                                                                        fontWeight: "medium",
                                                                        ...(!policy.isActive && {
                                                                            backgroundColor: this.props.darkMode ? "rgba(255, 255, 255, 0.12)" : undefined,
                                                                            color: this.props.darkMode ? "#ffffff" : undefined,
                                                                        }),
                                                                        ...(policy.isActive && {
                                                                            backgroundColor: "#4CAF50",
                                                                            color: "#ffffff",
                                                                            "& .MuiChip-label": {
                                                                                color: "#ffffff",
                                                                            },
                                                                        }),
                                                                    }}
                                                                />
                                                            </MDBox>
                                                        </MDBox>

                                                        <Divider sx={{ my: 2 }} />

                                                        <MDBox mb={1.5} flexGrow={1}>
                                                            <MDTypography variant="h4" color="info" display="block">
                                                                {policy.limitValue.toLocaleString()}P
                                                            </MDTypography>
                                                            <MDTypography variant="caption" color="text" display="block">
                                                                코드: {policy.limitCode}
                                                            </MDTypography>
                                                            <MDTypography variant="caption" color="text" display="block">
                                                                기간: {policy.startDate} ~ {policy.endDate}
                                                            </MDTypography>

                                                            {policy.description && (
                                                                <MDBox mt={1.5}>
                                                                    <MDTypography variant="caption" color="text">
                                                                        {policy.description}
                                                                    </MDTypography>
                                                                </MDBox>
                                                            )}
                                                        </MDBox>

                                                        <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                                            <MDBox>
                                                                <MDTypography variant="caption" color="text" display="block">
                                                                    테넌트: {policy.tenantName}
                                                                </MDTypography>
                                                            </MDBox>
                                                            <MDBox display="flex" gap={0.5}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        this.handleEditOpen(policy);
                                                                    }}
                                                                    sx={{
                                                                        color: this.props.darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                                                        "&:hover": {
                                                                            backgroundColor: this.props.darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.04)",
                                                                            color: "#49a3f1",
                                                                        },
                                                                    }}
                                                                >
                                                                    <Icon fontSize="small">edit</Icon>
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        this.handleDelete(policy.seqId);
                                                                    }}
                                                                    sx={{
                                                                        color: this.props.darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                                                        "&:hover": {
                                                                            backgroundColor: this.props.darkMode ? "rgba(255, 100, 100, 0.1)" : "rgba(255, 0, 0, 0.04)",
                                                                            color: "#f44336",
                                                                        },
                                                                    }}
                                                                >
                                                                    <Icon fontSize="small">delete</Icon>
                                                                </IconButton>
                                                            </MDBox>
                                                        </MDBox>

                                                    </MDBox>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>

                                {totalCount > 0 && (
                                    <MDBox mt={4} display="flex" justifyContent="center">
                                        <Pagination
                                            count={Math.ceil(totalCount / pageSize)}
                                            page={pageNumber}
                                            onChange={(_, page) => this.handlePageChange(page - 1)}
                                            color="primary"
                                            size="large"
                                            showFirstButton
                                            showLastButton
                                            sx={{
                                                '& .MuiPaginationItem-root': {
                                                    color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                                                },
                                                '& .Mui-selected': {
                                                    backgroundColor: '#49a3f1 !important',
                                                    color: '#ffffff !important',
                                                },
                                            }}
                                        />
                                    </MDBox>
                                )}
                            </>
                        )}
                    </MDBox>
                </MDBox>
                <Footer />
                <AddPointLimitPolicyDialog
                    open={this.state.addDialogOpen}
                    onClose={this.handleDialogClose}
                    onSuccess={this.handleSuccess}
                    editPolicy={this.state.editPolicy}
                    darkMode={this.props.darkMode}
                />
            </DashboardLayout>
        );
    }
}

function PointLimitPolicyListScreen(props: any) {
    const [controller] = useMaterialUIController();
    const { darkMode, miniSidenav } = controller;

    return <PointLimitPolicyListScreenClass {...props} darkMode={darkMode} miniSidenav={miniSidenav} />;
}

export default PointLimitPolicyListScreen;
